import React, { useMemo } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import type { ErpInputTableActions, RepeatRowsPayload } from '@/components/custom/erp';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { SelectedDetailCellInfo, useDetailPaste } from '../hooks';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import type { FormFieldState } from '../../../hooks';
import { getDetailTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedDetailCellInfo | null;
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  state: FormFieldState;
  constraints?: AccountConstraint[];
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

const detail = (row: Record<string, MuaHangRecord>) => ({
  ma_dv: row.ma_dv_data?.uuid || row.ma_dv || '',
  tk_vt: row.tk_vt_data?.uuid || row.ma_dv_data?.tk_cp_data?.uuid || row.tk_vt || '',
  so_luong: row.so_luong,
  ...constraintValues(row)
});

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  state,
  constraints = [],
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });

  const columns = useMemo(
    () => getDetailTableColumns(formMode, state, onCellValueChange, cellError),
    [cellError, formMode, state, onCellValueChange]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const actions = useMemo<ErpInputTableActions>(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down')
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu={true}
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => {
            console.log('Copy data per column:', text);
          }
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actions={actions}
      actionsDisabled={formMode === 'view'}
      actionLoading={isPasting}
      actionOrder={['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown']}
      pasteTarget={selectedCell ? { id: selectedCell.id, field: selectedCell.field } : null}
    />
  );
};
